// =====================================================
// contexts/AuthProvider.jsx
// =====================================================
// Hauptkomponente für die Authentifizierung
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { AuthContext } from './AuthContext';
import { ApolloContext } from './ApolloContext';
import { ApolloProvider as BaseApolloProvider } from '@apollo/client';

// Services importieren
import { logger } from '../services/auth/Logger';
import { TokenService } from '../services/auth/TokenService';
import { CognitoService } from '../services/auth/CognitoService';
import { DirectCognitoApolloService } from '../services/auth/DirectCognitoApolloService';
import { AuthService } from '../services/auth/AuthService';

// UI-Komponenten
import Loader from '../Loader'; // Anpassen an deine Komponente

// Umgebungsvariablen
const NEW_GRAPHQL_ENDPOINT = process.env.REACT_APP_NEW_GRAPHQL_ENDPOINT;
const AUTHENTICATED_ENDPOINT =
  process.env.REACT_APP_USE_GRAPHQL_SERVER_AUTHENTICATED;
const UNAUTHENTICATED_ENDPOINT =
  process.env.REACT_APP_USE_GRAPHQL_SERVER_NOT_AUTHENTICATED;

const AuthProvider = ({ children, userPool }) => {
  // Zustand für Auth-Status und Apollo Client
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [idTokenPayload, setIdTokenPayload] = useState(null);
  const [hasOrganizerAccess, setHasOrganizerAccess] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [apolloClient, setApolloClient] = useState(null);
  const [currentClientType, setCurrentClientType] = useState('direct');

  // Refs für Services, um Neuerstellung bei Re-Renders zu vermeiden
  const servicesRef = useRef(null);

  // Services initialisieren (nur einmal)
  if (!servicesRef.current) {
    logger.debug('Initializing auth services');

    // Token Service für zentrale Token-Verwaltung
    const tokenService = new TokenService({
      ttl: {
        idToken: 30 * 60 * 1000, // 30 Minuten
        accessToken: 5 * 60 * 1000, // 5 Minuten
        refreshToken: 14 * 24 * 60 * 60 * 1000 // 14 Tage
      },
      refreshThreshold: 5 * 60 * 1000 // 5 Minuten vor Ablauf
    });

    // Cognito Service
    const cognitoService = new CognitoService(userPool, {
      tokenService
    });

    // Direct Cognito Apollo Service
    const directApolloService = new DirectCognitoApolloService({
      graphqlEndpoint: NEW_GRAPHQL_ENDPOINT,
      authenticatedEndpoint: AUTHENTICATED_ENDPOINT,
      unauthenticatedEndpoint: UNAUTHENTICATED_ENDPOINT,
      useNewGraphQLServer: true,
      cognitoService,
      tokenService,
      onAuthError: () => {
        // Zur Login-Seite weiterleiten bei Auth-Fehlern
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
      }
      // authService wird später hinzugefügt
    });

    // Haupt-Auth-Service
    const authService = new AuthService({
      tokenService,
      cognitoService,
      apolloService: directApolloService,
      maxBatchSize: 5, // Maximal 5 gleichzeitige API-Anfragen für Orga-Namen
      batchTimeout: 50 // 50ms Timeout für Batch-Anfragen
    });

    // authService dem Apollo Service hinzufügen
    directApolloService.authService = authService;

    servicesRef.current = {
      tokenService,
      cognitoService,
      directApolloService,
      authService
    };
  }

  const { tokenService, cognitoService, directApolloService, authService } =
    servicesRef.current;

  // Auth-Status-Änderungen überwachen
  useEffect(() => {
    logger.debug('Setting up auth state change listener');

    const unsubscribe = authService.addEventListener(
      'state:change',
      (state) => {
        setIsAuthenticated(state.isAuthenticated);
        setIsAnonymous(state.isAnonymous);
        setIsLoading(state.isLoading);
        setUserId(state.userId);
        setUserGroups(state.userGroups);
        setHasOrganizerAccess(state.hasOrganizerAccess);
        setOrganizations(state.organizations);

        // User- und Token-Informationen aus dem TokenService abrufen
        const idTokenPayload = tokenService.getTokenPayload('idToken');
        if (idTokenPayload) {
          setIdTokenPayload(idTokenPayload);
          const token = tokenService.getToken('idToken');
          setIdToken(token);

          // Expose token for access in restApi.js (both WebView and browser)
          if (token) {
            window.__authIdToken = token;
            logger.debug('Exposed idToken to window for authentication access');
          }

          // User-Informationen aus Cognito
          setUser(cognitoService.getCurrentUser());
        } else {
          setIdTokenPayload(null);
          setIdToken(null);
          setUser(null);

          // Clear exposed token
          window.__authIdToken = null;
        }
      }
    );

    return unsubscribe;
  }, []);

  // Apollo Client-Reset bei Auth-Statusänderungen
  useEffect(() => {
    logger.debug('Setting up Apollo cache reset listener');

    const unsubscribe = authService.addEventListener(
      'apollo:resetCache',
      async () => {
        if (apolloClient) {
          try {
            logger.debug('Resetting Apollo client cache');
            await apolloClient.clearStore();
          } catch (error) {
            logger.error('Error resetting Apollo cache', error);
          }
        }
      }
    );

    return unsubscribe;
  }, [apolloClient]);

  // WebView Authentication Handler
  useEffect(() => {
    console.log('🔧 Setting up WebView authentication handler in AuthProvider');
    logger.debug('Setting up WebView authentication handler');

    const handleWebViewAuth = async (event) => {
      try {
        const sessionData = event.detail;
        console.log('📱 AuthProvider received WebView auth data');
        logger.debug('Processing WebView authentication data in AuthProvider');

        // Use AuthService to process WebView authentication
        await authService.processWebViewAuthentication(sessionData);

        // Recreate Apollo client after authentication
        console.log('🔄 Recreating Apollo client after WebView authentication');
        const client = createApolloClientBasedOnEnvironment();
        setApolloClient(client);

        console.log('✅ WebView authentication completed in AuthProvider');
        logger.debug(
          'WebView authentication completed successfully in AuthProvider'
        );

        // Perform post-authentication debug check
        setTimeout(() => {
          performPostAuthDebugCheck();
        }, 1000);
      } catch (error) {
        console.error(
          '❌ Error processing WebView authentication in AuthProvider:',
          error
        );
        logger.error(
          'Error processing WebView authentication in AuthProvider',
          error
        );
      }
    };

    const handleWebViewAuthError = (event) => {
      console.error(
        '❌ WebView auth error received in AuthProvider:',
        event.detail
      );
      logger.error('WebView auth error in AuthProvider', event.detail);
    };

    const handleWebViewAuthTimeout = (event) => {
      console.error('⏰ WebView auth timeout in AuthProvider:', event.detail);
      logger.error('WebView auth timeout in AuthProvider', event.detail);
    };

    // Helper function to create Apollo client
    const createApolloClientBasedOnEnvironment = () => {
      console.log('🚀 Creating direct Cognito Apollo client');
      logger.debug('Creating direct Cognito Apollo client');
      setCurrentClientType('direct');
      return directApolloService.createApolloClient();
    };

    const performPostAuthDebugCheck = async () => {
      try {
        console.log('🔍 Performing post-authentication debug check...');

        // Check authentication state in AuthProvider
        console.log('Auth state in AuthProvider:', {
          isAuthenticated,
          isAnonymous,
          isLoading,
          userId,
          userGroupsCount: userGroups.length,
          hasOrganizerAccess,
          organizationsCount: organizations.length,
          apolloClientExists: !!apolloClient,
          currentClientType
        });

        // Check tokens in TokenService
        const idToken = tokenService.getToken('idToken');

        console.log('Token state:', {
          hasIdToken: !!idToken,
          idTokenLength: idToken?.length
        });

        // Test Apollo client if available
        if (apolloClient) {
          console.log('Apollo client state:', {
            clientType: currentClientType,
            cacheSize: apolloClient.cache?.data?.data
              ? Object.keys(apolloClient.cache.data.data).length
              : 0
          });
        }

        console.log('✅ Post-authentication debug check completed');
      } catch (error) {
        console.warn('⚠️ Post-authentication debug check failed:', error);
      }
    };

    // Add global handler for React Native WebView
    window.handleMobileAuth = async (sessionData) => {
      console.log('📱 Received auth data from mobile app via global handler');
      await handleWebViewAuth({ detail: sessionData });
    };

    // Listen for WebView authentication events
    window.addEventListener('mobileAuthReady', handleWebViewAuth);
    window.addEventListener('mobileAuthError', handleWebViewAuthError);
    window.addEventListener('mobileAuthTimeout', handleWebViewAuthTimeout);

    return () => {
      window.removeEventListener('mobileAuthReady', handleWebViewAuth);
      window.removeEventListener('mobileAuthError', handleWebViewAuthError);
      window.removeEventListener('mobileAuthTimeout', handleWebViewAuthTimeout);
      delete window.handleMobileAuth;
    };
  }, []);

  // Initialisierung beim Laden
  useEffect(() => {
    logger.debug('Initializing auth provider');

    const initialize = async () => {
      try {
        // Skip normal initialization if we're in WebView and should wait for mobile auth
        const isWebView = window.ReactNativeWebView;
        if (isWebView) {
          logger.debug('WebView detected, waiting for mobile authentication');
          // Still create an anonymous Apollo client for initial loading
          const client = createApolloClientBasedOnEnvironment();
          setApolloClient(client);

          // Request authentication from mobile app after short delay
          setTimeout(() => {
            logger.debug('Requesting mobile authentication');
            try {
              // Only use React Native postMessage in WebView environment
              if (
                window.ReactNativeWebView &&
                window.ReactNativeWebView.postMessage
              ) {
                logger.debug('Using React Native postMessage');
                window.ReactNativeWebView.postMessage(
                  JSON.stringify({
                    type: 'requestMobileAuth'
                  })
                );
              } else if (!window.ReactNativeWebView) {
                // Only use event dispatch in regular web browsers (not WebView)
                logger.debug('Using event dispatch fallback for web browser');
                window.dispatchEvent(new Event('requestMobileAuth'));
              } else {
                logger.debug(
                  'WebView environment detected but postMessage not available'
                );
              }
            } catch (error) {
              logger.error('Failed to request mobile authentication', error);
            }
          }, 1000);

          return;
        }

        // Normal initialization for web browsers
        await authService.initialize();

        // Präventive Token-Prüfung vor Apollo-Client-Erstellung
        await validateTokensBeforeClientCreation();

        // Apollo Client erstellen basierend auf Environment Flag
        const client = createApolloClientBasedOnEnvironment();
        setApolloClient(client);
      } catch (error) {
        logger.error('Error initializing auth provider', error);

        // No fallback needed for direct authentication

        // Apollo Client trotzdem erstellen
        const client = createApolloClientBasedOnEnvironment();
        setApolloClient(client);
      }
    };

    // Präventive Token-Prüfung vor Apollo-Client-Erstellung
    const validateTokensBeforeClientCreation = async () => {
      try {
        // Prüfen, ob gültige Tokens vorhanden sind
        const idToken = tokenService.getToken('idToken');

        // Token-Format validieren
        const idTokenValid = idToken && tokenService.isValidJwtFormat(idToken);

        logger.debug(
          `Tokens before client creation - ID Token valid: ${idTokenValid}`
        );

        // Token-Typen prüfen
        if (idToken) {
          const idTokenType = tokenService.getTokenType(idToken);
          if (idTokenType !== 'cognito_id_token') {
            logger.warn(
              `ID token has unexpected type: ${idTokenType}, expected cognito_id_token`
            );
          }
        }
      } catch (error) {
        logger.error('Error validating tokens before client creation', error);
        // Fehler nicht weiterleiten, damit die Initialisierung fortgesetzt wird
      }
    };

    // Helper function to create Apollo client
    const createApolloClientBasedOnEnvironment = () => {
      logger.debug('Creating direct Cognito Apollo client');
      setCurrentClientType('direct');
      return directApolloService.createApolloClient();
    };

    initialize();

    // Cleanup
    return () => {
      logger.debug('Cleaning up auth provider');
      logger.destroy();
    };
  }, []);

  // Automatische Token-Aktualisierung und WebView Token Expiry Handling
  useEffect(() => {
    // Nur für authentifizierte, nicht-anonyme Benutzer
    if (isAuthenticated && !isAnonymous) {
      logger.debug('Setting up token refresh interval');

      // Alle 15 Minuten aktualisieren
      const REFRESH_INTERVAL = 15 * 60 * 1000;

      const intervalId = setInterval(async () => {
        try {
          logger.debug('Proactively refreshing tokens');

          // Check if we're in WebView and if tokens are about to expire
          const isWebView = window.ReactNativeWebView;

          if (isWebView) {
            // For WebView, check token expiry and request new tokens from mobile app
            const idToken = tokenService.getToken('idToken');
            if (idToken) {
              const payload = tokenService.getTokenPayload('idToken');
              const expiresAt = payload?.exp * 1000; // Convert to milliseconds
              const now = Date.now();
              const fiveMinutes = 5 * 60 * 1000;

              if (expiresAt && expiresAt - now < fiveMinutes) {
                logger.debug(
                  'Token expiring soon in WebView, requesting refresh from mobile app'
                );
                window.dispatchEvent(new Event('requestMobileAuth'));
                return; // Skip normal token refresh
              }
            }
          }

          // Normal token refresh for web browsers or when tokens are not expiring soon
          await authService.refreshTokens();
        } catch (error) {
          logger.error('Error during proactive token refresh', error);

          // If we're in WebView and refresh fails, request new tokens from mobile app
          const isWebView = window.ReactNativeWebView;
          if (isWebView) {
            logger.debug(
              'Token refresh failed in WebView, requesting new tokens from mobile app'
            );
            window.dispatchEvent(new Event('requestMobileAuth'));
          }
        }
      }, REFRESH_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [isAuthenticated, isAnonymous]);

  // Auth-Kontext-Wert
  const authContextValue = useMemo(
    () => ({
      isAuthenticated,
      isAnonymous,
      isLoading,
      idToken,
      idTokenPayload,
      userId,
      userGroups,
      user,
      hasOrganizerAccess,
      organizations,

      // Login-Funktion
      login: async (username, password) => {
        try {
          return await authService.login(username, password);
        } catch (error) {
          logger.error('Login error in context', error);
          throw error;
        }
      },

      // Logout-Funktion
      logout: async (redirectToLogin = false) => {
        try {
          return await authService.logout(redirectToLogin);
        } catch (error) {
          logger.error('Logout error in context', error);
          throw error;
        }
      },

      // Token-Aktualisierung
      refreshSession: async () => {
        try {
          return await authService.refreshTokens();
        } catch (error) {
          logger.error('Refresh session error in context', error);
          throw error;
        }
      },

      // Auth-Daten neu abrufen
      refetchAuthData: async () => {
        try {
          // Sitzung neu abrufen
          const session = await cognitoService.getSession();

          if (session) {
            // Auth-Daten aktualisieren
            await authService.updateAuthData(session);

            return {
              isAuthenticated: true,
              userId: session.idToken.payload['custom:userId'],
              userGroups: session.idToken.payload['cognito:groups'] || [],
              hasOrganizerAccess: authService.checkOrganizerAccess(
                session.idToken.payload['cognito:groups'] || []
              ),
              organizations: authService.extractOrganizationIds(
                session.idToken.payload['cognito:groups'] || []
              ),
              session
            };
          } else {
            return {
              isAuthenticated: false,
              userId: null,
              userGroups: [],
              hasOrganizerAccess: false,
              organizations: [],
              session: null
            };
          }
        } catch (error) {
          logger.error('Refetch auth data error in context', error);
          throw error;
        }
      },

      // Prüfen, ob das Cognito-Refresh-Token gültig ist
      isCognitoRefreshTokenValid: async () => {
        try {
          return await cognitoService.isRefreshTokenValid();
        } catch (error) {
          logger.error('Error checking Cognito refresh token', error);
          return false;
        }
      },

      // WebView Authentication Processing
      processWebViewAuth: async (sessionData) => {
        try {
          return await authService.processWebViewAuthentication(sessionData);
        } catch (error) {
          logger.error('WebView auth error in context', error);
          throw error;
        }
      },

      // Check if running in WebView
      isWebView: () => Boolean(window.ReactNativeWebView),

      // Debug utilities for WebView authentication
      debugWebViewAuth: {
        // Get current authentication state
        getAuthState: () => ({
          isAuthenticated,
          isAnonymous,
          isLoading,
          userId,
          userGroups,
          hasOrganizerAccess,
          organizations,
          apolloClientExists: !!apolloClient,
          currentClientType
        }),

        // Get token state
        getTokenState: async () => {
          const idToken = tokenService.getToken('idToken');
          return {
            hasIdToken: !!idToken,
            idTokenLength: idToken?.length,
            idTokenPayload: idToken
              ? tokenService.getTokenPayload('idToken')
              : null
          };
        },

        // Test API connectivity
        testApiConnectivity: async () => {
          try {
            return await authService.testApiConnectivity();
          } catch (error) {
            console.error('API test failed:', error);
            return false;
          }
        },

        // Test authentication with dummy data
        testWithDummyData: async () => {
          try {
            const dummyData = {
              idToken:
                'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjk5OTk5OTk5OTl9.Lx4vJI1POWakjPvFr5nMDqWQwzVNJhKgI3k6YBNj7eI',
              accessToken:
                'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjk5OTk5OTk5OTl9.Lx4vJI1POWakjPvFr5nMDqWQwzVNJhKgI3k6YBNj7eI',
              refreshToken: 'dummy-refresh-token',
              expiresAt: Date.now() + 3600000,
              idTokenPayload: {
                sub: '12345',
                email: 'test@example.com',
                'cognito:username': 'testuser',
                'cognito:groups': ['test-group'],
                exp: 9999999999,
                iat: 1516239022
              }
            };

            await authService.processWebViewAuthentication(dummyData);
            return true;
          } catch (error) {
            console.error('Dummy test failed:', error);
            return false;
          }
        },

        // Force request mobile auth
        requestMobileAuth: () => {
          if (window.requestMobileAuth) {
            window.requestMobileAuth();
          } else {
            window.dispatchEvent(new Event('requestMobileAuth'));
          }
        },

        // Get environment info
        getEnvironmentInfo: () => ({
          isWebView: Boolean(window.ReactNativeWebView),
          userAgent: navigator.userAgent,
          windowFeatures: {
            handleMobileAuth: typeof window.handleMobileAuth,
            requestMobileAuth: typeof window.requestMobileAuth,
            debugWebViewAuth: typeof window.debugWebViewAuth
          },
          envVars: {
            newGraphQLEndpoint: NEW_GRAPHQL_ENDPOINT,
            backendUri: process.env.REACT_APP_BACKEND_URI
          }
        })
      },

      // Aktueller Client Type
      getCurrentClientType: () => currentClientType
    }),
    [
      isAuthenticated,
      isAnonymous,
      isLoading,
      idToken,
      idTokenPayload,
      userId,
      userGroups,
      user,
      hasOrganizerAccess,
      organizations,
      apolloClient,
      currentClientType
    ]
  );

  // Apollo Kontext-Wert
  const apolloContextValue = useMemo(
    () => ({
      client: apolloClient,
      clientType: currentClientType,

      getCurrentClientType: () => currentClientType,

      // Environment information
      newGraphqlEndpoint: NEW_GRAPHQL_ENDPOINT
    }),
    [apolloClient, currentClientType]
  );

  // Wenn noch geladen wird oder kein Apollo Client vorhanden ist, Ladeindikator anzeigen
  if (!apolloClient || isLoading) {
    return <Loader />;
  }

  // Provider rendern
  return (
    <AuthContext.Provider value={authContextValue}>
      <ApolloContext.Provider value={apolloContextValue}>
        <BaseApolloProvider client={apolloClient}>
          {children}
        </BaseApolloProvider>
      </ApolloContext.Provider>
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  userPool: PropTypes.object.isRequired
};

export default AuthProvider;
